import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  RefreshCw, 
  Search,
  ArrowLeft,
  Mail,
  Phone,
  Users,
  Building,
  Calendar,
  BookOpen,
  MoreHorizontal,
  MapPin,
  ExternalLink
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ClassSponsor, ClassSponsorStatus, CustomerContact } from '@/types';
import { Badge } from '@/components/ui/badge';
import { formatDate, cn } from '@/lib/utils';

interface ClassSponsorWithRelations extends ClassSponsor {
  customer_contacts?: {
    id: string;
    first_name: string;
    last_name: string | null;
    email: string;
    phone: string | null;
  };
}

interface ClassDetails {
  id: string;
  course_id?: string;
  status: string;
  location_address?: string;
  created_at?: string;
  courses?: {
    name: string;
    description?: string;
  };
  sessions?: {
    id: string;
    start_date: string;
    end_date: string;
    instructor_id: string;
    instructors?: {
      first_name: string;
      last_name: string;
    };
  }[];
}

const ClassSponsors = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState<ClassSponsorWithRelations[]>([]);
  const [classInfo, setClassInfo] = useState<ClassDetails | null>(null);
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [classLoading, setClassLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeTab, setActiveTab] = useState('sponsors');
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentSponsor, setCurrentSponsor] = useState<ClassSponsorWithRelations | null>(null);
  
  // Form states
  const [formContactId, setFormContactId] = useState<string | undefined>(undefined);
  const [formStatus, setFormStatus] = useState<ClassSponsorStatus>('active');

  useEffect(() => {
    if (classId) {
      fetchClassInfo();
      fetchSponsors();
      fetchContacts();
    }
  }, [classId]);

  const fetchClassInfo = async () => {
    setClassLoading(true);
    try {
      const { data, error } = await supabase
        .from('classes')
        .select(`
          *,
          courses(name, description),
          sessions:class_sessions(
            id, start_date, end_date, instructor_id,
            instructors:instructor_id(first_name, last_name)
          )
        `)
        .eq('id', classId)
        .single();

      if (error) throw error;
      
      setClassInfo(data as ClassDetails);
    } catch (error) {
      console.error('Erro ao buscar informações da turma:', error);
      toast.error('Não foi possível carregar as informações da turma');
    } finally {
      setClassLoading(false);
    }
  };

  const fetchSponsors = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('class_sponsors')
        .select('*, customer_contacts(id, first_name, last_name, email, phone)')
        .eq('class_id', classId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setSponsors(data);
    } catch (error) {
      console.error('Erro ao buscar patrocinadores:', error);
      toast.error('Não foi possível carregar os patrocinadores');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .order('first_name');

      if (error) throw error;
      
      setContacts(data);
    } catch (error) {
      console.error('Erro ao buscar contatos:', error);
      toast.error('Não foi possível carregar os contatos');
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      fetchSponsors();
      return;
    }

    const filteredSponsors = sponsors.filter(sponsor => 
      sponsor.customer_contacts?.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sponsor.customer_contacts?.last_name && sponsor.customer_contacts.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      sponsor.customer_contacts?.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setSponsors(filteredSponsors);
  };

  const openAddDialog = () => {
    setFormContactId(undefined);
    setFormStatus('active');
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (sponsor: ClassSponsorWithRelations) => {
    setCurrentSponsor(sponsor);
    setFormContactId(sponsor.customer_contact_id);
    setFormStatus(sponsor.status as ClassSponsorStatus);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (sponsor: ClassSponsorWithRelations) => {
    setCurrentSponsor(sponsor);
    setIsDeleteDialogOpen(true);
  };

  const handleAddSponsor = async () => {
    if (!formContactId) {
      toast.error('O contato é obrigatório');
      return;
    }

    // Check if this contact is already a sponsor for this class
    const existingSponsor = sponsors.find(s => s.customer_contact_id === formContactId);
    if (existingSponsor) {
      toast.error('Este contato já é um patrocinador desta turma');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('class_sponsors')
        .insert([
          { 
            class_id: classId,
            customer_contact_id: formContactId,
            status: formStatus
          }
        ])
        .select();

      if (error) throw error;

      toast.success('Patrocinador adicionado com sucesso');
      setIsAddDialogOpen(false);
      fetchSponsors();
    } catch (error) {
      console.error('Erro ao adicionar patrocinador:', error);
      toast.error('Não foi possível adicionar o patrocinador');
    }
  };

  const handleEditSponsor = async () => {
    if (!currentSponsor) {
      return;
    }

    try {
      const { error } = await supabase
        .from('class_sponsors')
        .update({ 
          status: formStatus,
          updated_at: new Date().toISOString()
        })
        .eq('class_id', currentSponsor.class_id)
        .eq('customer_contact_id', currentSponsor.customer_contact_id);

      if (error) throw error;

      toast.success('Patrocinador atualizado com sucesso');
      setIsEditDialogOpen(false);
      fetchSponsors();
    } catch (error) {
      console.error('Erro ao atualizar patrocinador:', error);
      toast.error('Não foi possível atualizar o patrocinador');
    }
  };

  const handleDeleteSponsor = async () => {
    if (!currentSponsor) return;

    try {
      const { error } = await supabase
        .from('class_sponsors')
        .delete()
        .eq('class_id', currentSponsor.class_id)
        .eq('customer_contact_id', currentSponsor.customer_contact_id);

      if (error) throw error;

      toast.success('Patrocinador removido com sucesso');
      setIsDeleteDialogOpen(false);
      fetchSponsors();
    } catch (error) {
      console.error('Erro ao remover patrocinador:', error);
      toast.error('Não foi possível remover o patrocinador');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default">Ativo</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inativo</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getClassStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string, variant: "default" | "secondary" | "destructive" | "outline" }> = {
      draft: { label: 'Rascunho', variant: 'outline' },
      scheduled: { label: 'Agendada', variant: 'default' },
      cancelled: { label: 'Cancelada', variant: 'destructive' },
      finished: { label: 'Finalizada', variant: 'secondary' },
    };

    const statusInfo = statusMap[status] || { label: status, variant: 'outline' };
    
    return (
      <Badge variant={statusInfo.variant}>
        {statusInfo.label}
      </Badge>
    );
  };

  const getInitials = (name: string) => {
    if (!name) return 'T';
    const words = name.split(' ');
    if (words.length === 1) return name.charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  const getNextSessionInfo = () => {
    if (!classInfo?.sessions || classInfo.sessions.length === 0) return null;
    
    const now = new Date();
    const futureSessions = classInfo.sessions
      .filter(s => new Date(s.start_date) > now)
      .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    
    return futureSessions.length > 0 ? futureSessions[0] : null;
  };

  const goBack = () => {
    navigate('/classes');
  };

  const navigateToClassDetails = () => {
    navigate(`/classes/${classId}`);
  };

  if (classLoading) {
    return (
      <DashboardLayout>
        <div className="w-full p-6">
          <div className="flex flex-col gap-4 w-full max-w-7xl mx-auto">
            <Skeleton className="h-16 w-1/3" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
            <Skeleton className="h-64 mt-4" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header with class information */}
          <div className="bg-white dark:bg-gray-950 rounded-xl shadow-sm border p-6 mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 border-2">
                  <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                    {getInitials(classInfo?.courses?.name || 'Turma')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <h1 className="text-2xl font-extrabold">{classInfo?.courses?.name || 'Turma'}</h1>
                    {getClassStatusBadge(classInfo?.status || 'draft')}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-muted-foreground mt-1">
                    <span className="text-sm">ID: {classId}</span>
                    {classInfo?.location_address && (
                      <span className="text-sm flex items-center">
                        <span className="mx-1 hidden sm:inline">•</span>
                        <MapPin className="h-3 w-3 mr-1" />
                        <span>{classInfo.location_address}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 mt-2 md:mt-0">
                <Button variant="outline" size="sm" className="gap-1" onClick={goBack}>
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Voltar
                </Button>
                <Button variant="outline" size="sm" className="gap-1" onClick={navigateToClassDetails}>
                  <Calendar className="h-3.5 w-3.5" />
                  Detalhes da Turma
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1">
                      <MoreHorizontal className="h-3.5 w-3.5" />
                      Mais Ações
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={openAddDialog}>Adicionar Patrocinador</DropdownMenuItem>
                    <DropdownMenuItem onClick={fetchSponsors}>Atualizar Lista</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Tabs de navegação */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid grid-cols-2 sm:w-[400px]">
              <TabsTrigger value="sponsors">Patrocinadores</TabsTrigger>
              <TabsTrigger value="classInfo">Informações da Turma</TabsTrigger>
            </TabsList>
            
            {/* Tab: Patrocinadores */}
            <TabsContent value="sponsors" className="mt-4">
              <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-extrabold">Patrocinadores ({sponsors.length})</h2>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex w-full max-w-sm items-center space-x-2">
                    <div className="relative w-full">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <Input
                        placeholder="Buscar patrocinadores..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      />
                    </div>
                  </div>
                  <Button variant="outline" onClick={handleSearch} size="sm">
                    Buscar
                  </Button>
                  <Button variant="outline" onClick={fetchSponsors} size="icon" className="w-9 h-9">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button onClick={openAddDialog} className="gap-1">
                    <Plus className="h-4 w-4" /> Novo Patrocinador
                  </Button>
                </div>
              </div>

              <Card>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="p-8">
                      <div className="space-y-3">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    </div>
                  ) : sponsors.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12 text-center">
                      <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
                      <h3 className="text-lg font-extrabold mb-2">Nenhum patrocinador encontrado</h3>
                      <p className="text-muted-foreground mb-4">Adicione patrocinadores para esta turma.</p>
                      <Button onClick={openAddDialog} className="gap-1">
                        <Plus className="h-4 w-4" /> Novo Patrocinador
                      </Button>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Telefone</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sponsors.map((sponsor) => (
                          <TableRow 
                            key={`${sponsor.class_id}-${sponsor.customer_contact_id}`}
                            className="hover:bg-muted/50 transition-colors"
                          >
                            <TableCell className="font-extrabold">
                              {sponsor.customer_contacts?.first_name} {sponsor.customer_contacts?.last_name || ''}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                                <span>{sponsor.customer_contacts?.email || '-'}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {sponsor.customer_contacts?.phone ? (
                                <div className="flex items-center gap-1">
                                  <Phone className="h-4 w-4 text-muted-foreground" />
                                  <span>{sponsor.customer_contacts.phone}</span>
                                </div>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>{getStatusBadge(sponsor.status)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditDialog(sponsor)}
                                  title="Editar"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openDeleteDialog(sponsor)}
                                  title="Excluir"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Tab: Informações da Turma */}
            <TabsContent value="classInfo" className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Informações básicas */}
                <Card className="lg:col-span-1">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Informações da Turma</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start">
                      <BookOpen className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Curso</p>
                        <p className="font-extrabold">{classInfo?.courses?.name || 'Não definido'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Calendar className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <div>{getClassStatusBadge(classInfo?.status || 'draft')}</div>
                      </div>
                    </div>
                    
                    {classInfo?.location_address && (
                      <div className="flex items-start">
                        <MapPin className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Local</p>
                          <p className="font-extrabold">{classInfo.location_address}</p>
                        </div>
                      </div>
                    )}
                    
                    {classInfo?.created_at && (
                      <div className="flex items-start">
                        <Calendar className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Data de Criação</p>
                          <p className="font-extrabold">{formatDate(classInfo.created_at)}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Próxima sessão */}
                <Card className="lg:col-span-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Próxima Sessão</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {getNextSessionInfo() ? (
                      <div className="border rounded-lg p-4">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                          <div>
                            <p className="font-extrabold">
                              {formatDate(getNextSessionInfo()?.start_date || '')}
                            </p>
                            <div className="flex items-center text-muted-foreground mt-1">
                              <Calendar className="h-3.5 w-3.5 mr-1" />
                              <span>
                                {new Date(getNextSessionInfo()?.start_date || '').toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - 
                                {new Date(getNextSessionInfo()?.end_date || '').toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </span>
                            </div>
                          </div>
                          <div>
                            {getNextSessionInfo()?.instructors && (
                              <div className="flex items-center text-muted-foreground">
                                <Users className="h-3.5 w-3.5 mr-1" />
                                <span>
                                  {getNextSessionInfo()?.instructors.first_name} {getNextSessionInfo()?.instructors.last_name}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <p>Nenhuma sessão agendada.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Estatísticas */}
                <Card className="lg:col-span-3">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Patrocinadores por Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="text-sm text-muted-foreground">Patrocinadores Ativos</p>
                          <p className="text-2xl font-extrabold">
                            {sponsors.filter(s => s.status === 'active').length}
                          </p>
                        </div>
                        <div className="bg-green-50 dark:bg-green-950/50 text-green-600 dark:text-green-400 p-3 rounded-full">
                          <Users className="h-5 w-5" />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="text-sm text-muted-foreground">Patrocinadores Inativos</p>
                          <p className="text-2xl font-extrabold">
                            {sponsors.filter(s => s.status === 'inactive').length}
                          </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 p-3 rounded-full">
                          <Users className="h-5 w-5" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Add Sponsor Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Patrocinador</DialogTitle>
            <DialogDescription>
              Selecione um contato para adicionar como patrocinador desta turma.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="contact" className="text-right">
                  Contato*
                </Label>
                <Select
                  value={formContactId}
                  onValueChange={(value) => setFormContactId(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um contato" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name || ''} ({contact.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="status" className="text-right">
                  Status*
                </Label>
                <Select
                  value={formStatus}
                  onValueChange={(value) => setFormStatus(value as ClassSponsorStatus)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddSponsor}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sponsor Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Editar Patrocinador</DialogTitle>
            <DialogDescription>
              Atualize as informações do patrocinador.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="edit-contact" className="text-right">
                  Contato
                </Label>
                <Input
                  id="edit-contact"
                  value={`${currentSponsor?.customer_contacts?.first_name || ''} ${currentSponsor?.customer_contacts?.last_name || ''}`}
                  disabled
                  className="col-span-3"
                />
              </div>
              <div>
                <Label htmlFor="edit-status" className="text-right">
                  Status*
                </Label>
                <Select
                  value={formStatus}
                  onValueChange={(value) => setFormStatus(value as ClassSponsorStatus)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEditSponsor}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Sponsor Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Patrocinador</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este patrocinador da turma? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSponsor}>
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default ClassSponsors; 